import React, { useState } from 'react';
import { PencilIcon, TrashIcon, FaceSmileIcon } from '@heroicons/react/24/outline';

interface Post {
  id: string;
  content: string;
  isEdited: boolean;
  editedAt?: string;
  createdAt: string;
  author: {
    id: string;
    username: string;
    firstName: string;
    lastName: string;
    avatar?: string;
  };
  reactions: Reaction[];
}

interface Reaction {
  id: string;
  emoji: string;
  user: {
    id: string;
    username: string;
  };
}

interface ForumPostProps {
  post: Post;
  currentUserId: string | null;
  onEdit: (postId: string, content: string) => void;
  onDelete: (postId: string) => void;
  onReaction: (postId: string, emoji: string) => void;
  index: number;
}

const EMOJI_PICKER = ['👍', '❤️', '😂', '😮', '😢', '🎉', '🙏', '🔥'];

const ForumPost: React.FC<ForumPostProps> = ({
  post,
  currentUserId,
  onEdit,
  onDelete,
  onReaction,
  index,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState(post.content);
  const [showActions, setShowActions] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);

  const isOwnPost = currentUserId === post.author.id;

  const handleEdit = () => {
    if (editContent.trim() && editContent !== post.content) {
      onEdit(post.id, editContent);
      setIsEditing(false);
    }
  };

  const handleCancelEdit = () => {
    setEditContent(post.content);
    setIsEditing(false);
  };

  // Group reactions by emoji
  const groupedReactions = post.reactions.reduce((acc, reaction) => {
    if (!acc[reaction.emoji]) {
      acc[reaction.emoji] = {
        count: 0,
        users: [],
        hasUserReacted: false,
      };
    }
    acc[reaction.emoji].count++;
    acc[reaction.emoji].users.push(reaction.user.username);
    if (reaction.user.id === currentUserId) {
      acc[reaction.emoji].hasUserReacted = true;
    }
    return acc;
  }, {} as Record<string, { count: number; users: string[]; hasUserReacted: boolean }>);

  return (
    <div
      className="bg-white rounded-lg shadow hover:shadow-md transition-shadow"
      onMouseEnter={() => setShowActions(true)}
      onMouseLeave={() => {
        setShowActions(false);
        setShowEmojiPicker(false);
      }}
    >
      <div className="p-6">
        <div className="flex items-start gap-4">
          {/* Author Avatar */}
          <img
            src={
              post.author.avatar ||
              `https://ui-avatars.com/api/?name=${encodeURIComponent(
                post.author.firstName + ' ' + post.author.lastName
              )}`
            }
            alt={post.author.username}
            className="h-10 w-10 rounded-full flex-shrink-0"
          />

          {/* Post Content */}
          <div className="flex-1 min-w-0">
            {/* Author Info */}
            <div className="flex items-center justify-between mb-2">
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-gray-900">
                    {post.author.firstName} {post.author.lastName}
                  </span>
                  <span className="text-sm text-gray-500">#{index}</span>
                </div>
                <div className="text-xs text-gray-500 flex items-center gap-2">
                  <span>
                    {new Date(post.createdAt).toLocaleString()}
                  </span>
                  {post.isEdited && (
                    <>
                      <span>•</span>
                      <span>(edited)</span>
                    </>
                  )}
                </div>
              </div>

              {/* Actions */}
              {isOwnPost && showActions && !isEditing && (
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setIsEditing(true)}
                    className="p-1.5 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                    title="Edit"
                  >
                    <PencilIcon className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => onDelete(post.id)}
                    className="p-1.5 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                    title="Delete"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              )}
            </div>

            {/* Post Content */}
            {isEditing ? (
              <div className="mt-3">
                <textarea
                  value={editContent}
                  onChange={(e) => setEditContent(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                  rows={4}
                />
                <div className="mt-2 flex items-center gap-2">
                  <button
                    onClick={handleEdit}
                    className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 transition-colors"
                  >
                    Save
                  </button>
                  <button
                    onClick={handleCancelEdit}
                    className="px-3 py-1.5 bg-gray-100 text-gray-700 text-sm rounded hover:bg-gray-200 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div className="mt-3 prose prose-sm max-w-none">
                <p className="text-gray-700 whitespace-pre-wrap">
                  {post.content}
                </p>
              </div>
            )}

            {/* Reactions */}
            <div className="mt-4 flex items-center gap-2 flex-wrap">
              {Object.entries(groupedReactions).map(([emoji, data]) => (
                <button
                  key={emoji}
                  onClick={() => onReaction(post.id, emoji)}
                  className={`flex items-center gap-1 px-2 py-1 rounded-full text-sm transition-colors ${
                    data.hasUserReacted
                      ? 'bg-blue-100 border-2 border-blue-500'
                      : 'bg-gray-100 hover:bg-gray-200 border-2 border-transparent'
                  }`}
                  title={data.users.join(', ')}
                >
                  <span>{emoji}</span>
                  <span className="text-xs font-medium text-gray-700">
                    {data.count}
                  </span>
                </button>
              ))}

              {/* Add Reaction Button */}
              {currentUserId && (
                <div className="relative">
                  <button
                    onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                    className="p-1.5 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-full transition-colors"
                    title="Add reaction"
                  >
                    <FaceSmileIcon className="h-5 w-5" />
                  </button>

                  {/* Emoji Picker Popup */}
                  {showEmojiPicker && (
                    <div className="absolute left-0 mt-2 bg-white border border-gray-200 rounded-lg shadow-lg p-2 z-10">
                      <div className="flex gap-1">
                        {EMOJI_PICKER.map((emoji) => (
                          <button
                            key={emoji}
                            onClick={() => {
                              onReaction(post.id, emoji);
                              setShowEmojiPicker(false);
                            }}
                            className="p-2 hover:bg-gray-100 rounded text-xl transition-colors"
                          >
                            {emoji}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForumPost;
